import { LegalLayout, LegalSection } from "./LegalLayout";

const LAST_UPDATED = "6 de mayo de 2026";

export function TermsAndConditionsPage() {
  return (
    <>
      <title>Términos y Condiciones · HATO</title>
      <meta
        name="description"
        content="Términos y condiciones de uso de la plataforma HATO de gestión ganadera contable."
      />
      <meta name="robots" content="index, follow" />

      <LegalLayout title="Términos y Condiciones" lastUpdated={LAST_UPDATED}>
        <p>
          Bienvenido a HATO. Estos términos y condiciones regulan el uso de la plataforma de gestión
          ganadera contable disponible en mihato.co (en adelante, la &ldquo;plataforma&rdquo; o
          &ldquo;HATO&rdquo;). Al solicitar acceso, crear una cuenta o utilizar cualquier
          funcionalidad de HATO, aceptás expresamente estos términos. Te pedimos leerlos con
          atención antes de comenzar a usar el servicio.
        </p>

        <LegalSection number={1} title="Aceptación de los términos">
          <p>
            Estos términos constituyen un acuerdo entre vos, como usuario de la plataforma, y HATO.
            Al utilizar HATO, ya sea como dueño de cuenta, miembro invitado o solicitante de acceso,
            manifestás haber leído, comprendido y aceptado este documento en su totalidad.
          </p>
          <p>
            Si no estás de acuerdo con alguna de las disposiciones contenidas en estos términos, te
            pedimos abstenerte de usar la plataforma. La aceptación de estos términos es condición
            indispensable para acceder a cualquiera de las funcionalidades de HATO.
          </p>
        </LegalSection>

        <LegalSection number={2} title="Creación y uso de la cuenta">
          <p>
            El acceso a HATO se realiza mediante un proceso de solicitud previa. El interesado envía
            sus datos de contacto a través del formulario público de solicitud y nuestro equipo
            evalúa la solicitud antes de habilitar el acceso a la plataforma.
          </p>
          <p>
            Una vez aprobada la solicitud, la autenticación se realiza mediante enlaces de un solo
            uso enviados al correo electrónico registrado. No utilizamos contraseñas, por lo que la
            seguridad de tu cuenta depende directamente de la seguridad de tu correo electrónico. Es
            tu responsabilidad mantener el control de la cuenta de correo asociada a HATO y
            notificarnos de inmediato si sospechás un acceso no autorizado.
          </p>
          <p>
            Cada cuenta de HATO corresponde a una finca, operación o entidad ganadera individual,
            gestionada por un usuario titular. Los datos cargados en una cuenta son de uso exclusivo
            del titular y de los miembros que él mismo invite.
          </p>
        </LegalSection>

        <LegalSection number={3} title="Roles y miembros">
          <p>
            HATO permite que el titular de una cuenta invite a otras personas a colaborar en la
            gestión de la finca. Cada invitación se asocia a un rol específico que determina el
            alcance de las acciones que el miembro puede realizar dentro de la cuenta. Los roles
            disponibles son los siguientes:
          </p>
          <ul className="ml-2 flex list-inside list-disc flex-col gap-2 marker:text-neutral-500">
            <li>
              <span className="text-white">Owner (titular):</span> crea la cuenta, gestiona
              miembros, accede a toda la información operativa y contable, y es el único que puede
              dar de baja la cuenta.
            </li>
            <li>
              <span className="text-white">Admin:</span> tiene los mismos permisos operativos y
              contables que el titular, incluyendo la posibilidad de invitar nuevos miembros, salvo
              dar de baja la cuenta.
            </li>
            <li>
              <span className="text-white">Operator:</span> puede gestionar el hato y registrar
              eventos del día a día, pero no accede a información contable, costos ni reportes
              financieros.
            </li>
            <li>
              <span className="text-white">Viewer:</span> tiene acceso de solo lectura a la
              información de la cuenta, sin posibilidad de modificar registros.
            </li>
          </ul>
          <p>
            El titular es responsable de las invitaciones que envía y de revocar el acceso de
            miembros cuando ya no corresponda. Las acciones realizadas por un miembro dentro de la
            cuenta se entienden realizadas bajo la autorización del titular.
          </p>
        </LegalSection>

        <LegalSection number={4} title="Uso aceptable">
          <p>
            HATO está pensado para gestionar la información de tu hato y de tu operación contable
            ganadera. Podés cargar datos de animales, registrar eventos operativos, asignar costos,
            generar reportes y exportar información para tu contador.
          </p>
          <p>El uso de HATO está sujeto a las siguientes restricciones:</p>
          <ul className="ml-2 flex list-inside list-disc flex-col gap-2 marker:text-neutral-500">
            <li>
              No utilizar la plataforma para fines ilícitos, fraudulentos o contrarios a la moral o
              las buenas costumbres.
            </li>
            <li>
              No realizar extracciones masivas o sistemáticas de información mediante scripts, bots
              u otros medios automatizados sin autorización expresa.
            </li>
            <li>
              No intentar realizar ingeniería inversa, descompilar o vulnerar los mecanismos de
              seguridad del software.
            </li>
            <li>
              No introducir contenido malicioso, virus, código dañino o cualquier elemento que pueda
              comprometer la integridad de la plataforma o de otros usuarios.
            </li>
            <li>
              No suplantar la identidad de otra persona ni proveer información falsa al solicitar
              acceso o al cargar datos en la plataforma.
            </li>
          </ul>
          <p>
            El incumplimiento de cualquiera de estas reglas puede resultar en la suspensión o baja
            de la cuenta sin necesidad de aviso previo, así como en las acciones legales que
            correspondan.
          </p>
        </LegalSection>

        <LegalSection number={5} title="Datos del hato y datos contables">
          <p>
            El cliente es el único titular de toda la información que carga en la plataforma,
            incluyendo datos de animales, registros operativos, costos, mappings de cuentas y
            exports generados. HATO procesa esa información exclusivamente para prestar el servicio
            y no la utiliza para fines distintos.
          </p>
          <p>
            En cualquier momento podés exportar tus datos en los formatos que la plataforma ofrece y
            solicitar la eliminación de tu cuenta. La eliminación de la cuenta conlleva la baja de
            la información asociada, salvo aquella que debamos retener por obligaciones legales o
            contables.
          </p>
          <p>
            Es tu responsabilidad mantener copias de respaldo de los exports contables generados,
            especialmente aquellos que entregás a tu contador o a la administración tributaria.
          </p>
        </LegalSection>

        <LegalSection number={6} title="Disponibilidad del servicio">
          <p>
            HATO opera bajo el principio de mejor esfuerzo y se compromete a mantener la plataforma
            disponible y operativa siguiendo prácticas estándar de la industria. Sin embargo, pueden
            ocurrir interrupciones temporales por mantenimiento programado, actualizaciones, fallas
            técnicas o eventos de fuerza mayor que escapen a nuestro control.
          </p>
          <p>
            Cuando sea posible, anunciaremos los mantenimientos programados con antelación a través
            de la plataforma o por correo electrónico. HATO se reserva el derecho de modificar,
            suspender o discontinuar funcionalidades del servicio para mejorar la experiencia
            general de uso, sin que ello genere derecho a indemnización.
          </p>
        </LegalSection>

        <LegalSection number={7} title="Suspensión y baja de cuenta">
          <p>
            Podés solicitar la baja de tu cuenta en cualquier momento escribiendo a{" "}
            <a
              href="mailto:info@mihato.co"
              className="text-white underline-offset-4 hover:underline"
            >
              info@mihato.co
            </a>
            . El proceso de baja contempla la entrega previa de los exports finales que necesités y
            la eliminación posterior de los datos asociados, en los términos descritos en la
            Política de Privacidad.
          </p>
          <p>
            HATO se reserva el derecho de suspender o dar de baja unilateralmente cuentas que
            incumplan estos términos, que realicen un uso indebido del servicio o que generen riesgo
            para la integridad de la plataforma o de otros usuarios. En estos casos notificaremos al
            titular por correo electrónico cuando sea razonablemente posible.
          </p>
        </LegalSection>

        <LegalSection number={8} title="Propiedad intelectual">
          <p>
            El software, el diseño visual, la marca, el código fuente y todos los elementos propios
            de la plataforma son propiedad exclusiva de HATO y están protegidos por la legislación
            costarricense y los tratados internacionales aplicables en materia de propiedad
            intelectual. Nada en estos términos debe interpretarse como una cesión total o parcial
            de esos derechos.
          </p>
          <p>
            La información que el cliente carga en la plataforma sigue siendo de su propiedad. HATO
            solo recibe una licencia limitada y revocable para procesar esa información con el fin
            de prestar el servicio.
          </p>
        </LegalSection>

        <LegalSection number={9} title="Limitación de responsabilidad">
          <p>
            HATO es una herramienta digital de gestión y registro. No actúa como asesor contable,
            tributario, financiero, legal ni veterinario. Los reportes y exports generados por la
            plataforma son insumos que el cliente debe revisar y validar con su contador, su asesor
            profesional o las autoridades correspondientes antes de tomar cualquier decisión basada
            en ellos.
          </p>
          <p>
            HATO no se responsabiliza por las decisiones contables, fiscales, sanitarias u
            operativas que el cliente tome a partir de la información presentada en la plataforma,
            ni por las consecuencias derivadas de información incorrecta o incompleta cargada por el
            propio usuario.
          </p>
          <p>
            Dentro del marco permitido por la legislación costarricense, HATO no será responsable
            por daños indirectos, lucro cesante, pérdida de oportunidades de negocio o cualquier
            otro daño consecuencial derivado del uso o imposibilidad de uso de la plataforma.
          </p>
        </LegalSection>

        <LegalSection number={10} title="Cambios a los términos">
          <p>
            HATO puede actualizar estos términos para reflejar cambios en la plataforma, en nuestras
            prácticas operativas o en la legislación aplicable. Cuando hagamos cambios relevantes te
            notificaremos a través de la plataforma o por correo electrónico, e indicaremos la fecha
            de la nueva versión al inicio del documento.
          </p>
          <p>
            El uso continuado del servicio después de la notificación implica la aceptación de la
            versión actualizada. Si no estás de acuerdo con los cambios, podés solicitar la baja de
            tu cuenta antes de que entren en vigor.
          </p>
        </LegalSection>

        <LegalSection number={11} title="Ley aplicable y jurisdicción">
          <p>
            Estos términos se rigen por las leyes de la República de Costa Rica. Cualquier
            controversia o disputa derivada de la interpretación, aplicación o ejecución de estos
            términos será sometida a la jurisdicción de los tribunales competentes de Costa Rica,
            renunciando las partes a cualquier otro fuero que pudiera corresponder.
          </p>
        </LegalSection>

        <p className="text-neutral-500 text-sm">
          Para consultas sobre estos términos escribinos a{" "}
          <a
            href="mailto:info@mihato.co"
            className="text-neutral-300 underline-offset-4 hover:text-white hover:underline"
          >
            info@mihato.co
          </a>
          .
        </p>
      </LegalLayout>
    </>
  );
}
